import { createRouter, createWebHistory } from 'vue-router'
import { useSupabase } from '../stores/supabase'
import { Capacitor } from '@capacitor/core'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(savedPosition ?? { top: 0 })
      }, 300)
    })
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/Home.vue'),
      meta: {
        position: 0,
        hideSidebar: () => {
          const supabase = useSupabase()

          return supabase.user.status !== 'SignedIn' && Capacitor.getPlatform() === 'web'
        },
        requiresAuth: Capacitor.getPlatform() !== 'web'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login.vue'),
      meta: {
        requiresGuest: true,
        hideSidebar: () => true,
        fullScreen: true
      }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/ResetPassword.vue'),
      meta: {
        hideSidebar: () => true,
        fullScreen: true
      }
    },
    {
      path: '/welcome',
      name: 'welcome',
      component: () => import('../views/Welcome.vue'),
      meta: {
        position: -1,
        requiresAuth: true,
        hideSidebar: () => true,
        fullScreen: true
      }
    },
    {
      path: '/trial-expired',
      name: 'trial-expired',
      component: () => import('../views/TrialExpired.vue'),
      meta: {
        requiresAuth: true,
        hideSidebar: () => true,
        fullScreen: true
      }
    },
    {
      path: '/standings',
      children: [
        {
          path: '',
          name: 'current-standings',
          component: () => import('../views/Standings.vue'),
          meta: {
            position: 2,
            requiresAuth: true
          }
        },
        {
          path: ':word_id',
          name: 'standings-word-id',
          component: () => import('../views/Standings.vue'),
          meta: {
            requiresAuth: true
          }
        }
      ]
    },
    {
      path: '/photos',
      children: [
        {
          path: '',
          name: 'photos',
          component: () => import('../views/Photos.vue'),
          meta: {
            position: 1,
            requiresAuth: true
          }
        },
        {
          path: ':photo_id',
          name: 'single-photo',
          component: () => import('../views/Photos/SinglePhoto.vue'),
          meta: {
            requiresAuth: false
          }
        }
      ]
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('../views/Notifications.vue'),
      meta: {
        position: 4,
        requiresAuth: true
      }
    },
    {
      path: '/profile',
      children: [
        {
          path: '',
          name: 'profile',
          redirect: () => {
            const supabase = useSupabase()

            if (supabase.user.status === 'SignedIn') {
              return {
                name: 'profile-username',
                params: {
                  username: supabase.user.data.username
                }
              }
            } else {
              return { name: 'login' }
            }
          }
        },
        {
          path: ':username',
          redirect: (to) => ({
            name: 'profile-username',
            params: {
              username: to.params.username
            }
          })
        }
      ]
    },
    {
      path: '/@',
      redirect: {
        name: 'profile'
      }
    },
    {
      path: '/@:username',
      name: 'profile-username',
      component: () => import('../views/Profile.vue'),
      meta: {
        position: 5,
        requiresAuth: false
      }
    },
    {
      path: '/settings',
      children: [
        {
          path: '',
          name: 'settings',
          component: () => import('../views/Settings.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'account',
          name: 'settings-account',
          component: () => import('../views/Settings/Account.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'appearance',
          name: 'settings-appearance',
          component: () => import('../views/Settings/Appearance.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'notifications',
          name: 'settings-notifications',
          component: () => import('../views/Settings/Notifications.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'subscription',
          name: 'settings-subscription',
          component: () => import('../views/Settings/Subscription.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'beta-codes',
          name: 'settings-beta-codes',
          component: () => import('../views/Settings/BetaCodes.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: 'about',
          name: 'settings-about',
          component: () => import('../views/Settings/About.vue'),
          meta: {
            requiresAuth: true
          }
        }
      ]
    },
    {
      path: '/upload',
      name: 'upload',
      component: () => import('../views/Upload.vue'),
      meta: {
        position: 10,
        requiresAuth: true
      }
    },
    {
      path: '/search',
      name: 'search',
      component: () => import('../views/Search.vue'),
      meta: {
        position: 3,
        requiresAuth: true
      }
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: () => import('../views/PrivacyPolicy.vue'),
      meta: {
        hideSidebar: () => true,
        requiresAuth: false
      }
    },
    {
      path: '/terms-of-service',
      name: 'terms-of-service',
      component: () => import('../views/TermsOfService.vue'),
      meta: {
        hideSidebar: () => true,
        requiresAuth: false
      }
    },
    {
      path: '/delete-account',
      name: 'delete-account',
      component: () => import('../views/DeleteAccount.vue'),
      meta: {
        hideSidebar: () => true,
        requiresAuth: false
      }
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const supabase = useSupabase()

  const isAuthenticated = supabase.user.status === 'SignedIn'
  const requiresAuth = to.meta.requiresAuth
  const requiresGuest = to.meta.requiresGuest

  // If route requires authentication and user is not signed in
  if (requiresAuth && !isAuthenticated) {
    next('/login')
    return
  }

  // If route requires guest (login page) and user is signed in
  // BUT allow navigation to login if coming from settings (logout flow)
  if (requiresGuest && isAuthenticated && from.path !== '/settings') {
    next('/')
    return
  }

  // If user is signed in and trying to access login page, redirect to home
  // BUT allow if coming from settings (logout flow)
  if (isAuthenticated && to.name === 'login' && from.path !== '/settings') {
    next('/')
    return
  }

  // Check if authenticated user is trying to access trial-expired page inappropriately
  if (supabase.user.status === 'SignedIn' && to.name === 'trial-expired') {
    try {
      // Check if user actually has an expired trial
      const { data: userSettings, error } = await supabase.api
        .from('user_settings')
        .select('subscription_status, subscription_current_period_end')
        .eq('user_id', supabase.user.data.id)
        .single()

      if (error) {
        next('/')
        return
      }

      const isTrialExpired =
        userSettings.subscription_status === 'trial' &&
        userSettings.subscription_current_period_end &&
        new Date(userSettings.subscription_current_period_end) < new Date()

      // If user doesn't have an expired trial, redirect to home
      if (!isTrialExpired) {
        next('/')
        return
      }
    } catch (error) {
      next('/')
      return
    }
  }

  // Check if authenticated user needs onboarding or has expired trial (except for welcome, trial-expired, and settings pages)
  if (
    supabase.user.status === 'SignedIn' &&
    to.name !== 'welcome' &&
    to.name !== 'trial-expired' &&
    !to.path.startsWith('/settings') &&
    requiresAuth
  ) {
    try {
      // Check if user needs onboarding or has expired trial
      const { data: userSettings, error } = await supabase.api
        .from('user_settings')
        .select(
          'needs_onboarding, subscription_status, subscription_tier, subscription_current_period_end'
        )
        .eq('user_id', supabase.user.data.id)
        .single()

      if (error) {
        // Ignore error
      } else if (userSettings) {
        const needsOnboarding = userSettings.needs_onboarding

        // Check if trial has expired
        const isTrialExpired =
          userSettings.subscription_status === 'trial' &&
          userSettings.subscription_current_period_end &&
          new Date(userSettings.subscription_current_period_end) < new Date()

        // Define what constitutes a valid subscription
        const hasValidSubscription =
          userSettings.subscription_status === 'active' ||
          (userSettings.subscription_status === 'trial' && !isTrialExpired) ||
          (userSettings.subscription_tier === 'beta' &&
            userSettings.subscription_status === 'active')

        // If trial has expired, redirect to trial-expired page
        if (isTrialExpired) {
          next('/trial-expired')
          return
        }

        // If user needs onboarding or doesn't have a valid subscription, redirect to welcome
        if (needsOnboarding || !hasValidSubscription) {
          next('/welcome')
          return
        }
      }
    } catch (error) {
      // Ignore error
    }
  }

  next()
})

export default router
